/*============================================================================
  Boost quick view -> mini cart

  The Boost quick view (collection/search pages) is not wired to a cart: with
  Boost's own cart disabled it redirects to /cart after adding. Rebuy Smart Cart
  has the cart page disabled, so /cart then bounces to /?viewcart=true (home +
  reload). Boost also starts that redirect before reading the add.js response,
  so the aborted read flashes "Failed!" even though the item was added.

  A network error, a non-JSON reply, 429 or 5xx is retried. A 422 where the
  item is in the cart anyway ("The maximum quantity of this item is already in
  your cart." when the last one is already there, or "Only N items were added
  due to availability.") counts as added: the mini cart opens with it. A 422
  without the item in the cart means there's no stock left: Shopify's message
  goes under the button (same markup as the PDP form error in theme.js).

  Take over the quick view add to cart: add via AJAX, close the quick view and
  open the mini cart in place.
==============================================================================*/
(() => {
  const selectors = {
    modal: '.boost-sd__modal-wrapper',
    addToCart: '.boost-sd__btn-add-to-cart:not(.boost-sd__button--preorder)',
    buttonText: '.boost-sd__button-text',
    quantity: '.boost-sd__input-number-input',
    quantityError: '.boost-sd__quick-view-quantity-error',
    close: '.boost-sd__modal-close-btn',
    buttonRow: '.boost-sd__quick-view-btn',
    stockMessage: '.boost-quick-view-cart__stock-message'
  };

  // Waits before each retry of a request that failed for a transient reason
  const retryDelays = [500, 1500];

  // Same labels Boost uses for its add to cart states
  const labels = {
    adding: 'Adding...',
    added: 'Added!',
    failed: 'Failed!'
  };

  const getLabel = btn => btn.querySelector(selectors.buttonText) ?? btn;

  const setLabel = (btn, text) => {
    getLabel(btn).textContent = text;
  };

  const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

  const isTransient = status => status === 429 || status >= 500;

  // POST to add.js, retrying network errors, non-JSON replies, 429 and 5xx.
  // Any other reply (200, 422, ...) is returned as is.
  const postAdd = async (cartAdd, variantId, quantity) => {
    for (let attempt = 0; ; attempt++) {
      try {
        // Rebuy watches fetch calls to /cart/add.js and refreshes the Smart Cart contents
        const response = await fetch(cartAdd, {
          method: 'POST',
          body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        const data = await response.json();

        if (!isTransient(response.status) || attempt >= retryDelays.length) {
          return { ok: response.ok, status: response.status, data };
        }
      } catch (error) {
        if (attempt >= retryDelays.length) {
          throw error;
        }
      }
      await wait(retryDelays[attempt]);
    }
  };

  const isInCart = async variantId => {
    const response = await fetch(window.theme?.routes?.cart ?? '/cart.js');
    const cart = await response.json();
    return cart.items.some(item => item.variant_id === variantId);
  };

  const clearStockMessage = modal => {
    modal.querySelector(selectors.stockMessage)?.remove();
  };

  // Same markup as the PDP form error (theme.js), so it picks up the theme's .errors style
  const showStockMessage = (btn, error) => {
    const message = typeof error?.description === 'string' ? error.description : error?.message;
    if (!message) {
      return;
    }

    const messageDiv = document.createElement('div');
    messageDiv.classList.add('errors', 'text-center', 'boost-quick-view-cart__stock-message');
    messageDiv.setAttribute('role', 'alert');
    messageDiv.textContent = message;
    // The button sits in a no-wrap flex row; put the message on its own line below it
    (btn.closest(selectors.buttonRow) ?? btn).after(messageDiv);
  };

  const openMiniCart = () => {
    // Keeps the header cart count in sync
    document.dispatchEvent(new CustomEvent('cart:build'));

    if (window.Rebuy?.SmartCart) {
      window.Rebuy.SmartCart.show();
    } else {
      document.dispatchEvent(new CustomEvent('cart:open'));
    }
  };

  const addToCart = async (btn, modal, variantId, quantity) => {
    const defaultText = getLabel(btn).textContent;
    const cartAdd = window.theme?.routes?.cartAdd ?? '/cart/add.js';

    btn.setAttribute('disabled', 'true');
    setLabel(btn, labels.adding);
    clearStockMessage(modal);

    try {
      const result = await postAdd(cartAdd, variantId, quantity);

      if (!result.ok) {
        if (result.status !== 422) {
          throw result.data;
        }

        // 422 without the item in the cart: no stock left, so say so under the button.
        // 422 with it: as many as the store allows are already there, so carry on and show them.
        if (!await isInCart(variantId)) {
          setLabel(btn, defaultText);
          showStockMessage(btn, result.data);
          return;
        }
      }

      setLabel(btn, labels.added);
      modal.querySelector(selectors.close)?.click();
      openMiniCart();
    } catch (error) {
      console.warn('Boost quick view add to cart failed', error);
      setLabel(btn, labels.failed);
    } finally {
      btn.removeAttribute('disabled');
      setTimeout(() => {
        if (btn.isConnected) {
          setLabel(btn, defaultText);
        }
      }, 500);
    }
  };

  // Capture phase on document runs before Boost's listener on the button.
  // stopPropagation (not stopImmediatePropagation) keeps Boost's own
  // document-level analytics listener recording the click.
  document.addEventListener('click', evt => {
    const btn = evt.target.closest(selectors.addToCart);
    const modal = btn?.closest(selectors.modal);
    const variantId = Number(btn?.dataset.variantId);

    // Leave anything we can't handle to Boost's default behavior
    if (!modal || !variantId) {
      return;
    }

    evt.preventDefault();
    evt.stopPropagation();

    const quantityInput = modal.querySelector(selectors.quantity);
    const quantity = quantityInput ? Number(quantityInput.value) : 1;

    if (!(quantity > 0)) {
      const quantityError = modal.querySelector(selectors.quantityError);
      if (quantityError) {
        quantityError.style.display = 'block';
      }
      return;
    }

    addToCart(btn, modal, variantId, quantity);
  }, true);
})();
